import { ApiError, Location, Session, TScriptId, TSessionId } from '@talon/web/lib/api';
import { SESSION_ESTIMATOR_TEMP_ESTIMATE_TITLE } from '@talon/web/src/config';
import { useEstimateStore, useProjectStore, useSessionStore } from '@talon/web/src/context';
import dayjs from 'dayjs';
import { Box } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState, SaveStatus, SessionEstimatorHeader, SessionRow } from '../..';
import { CreateSessionModal } from './CreateSessionModal';

export const SessionsPanel = observer(() => {
  /** Context **/
  const projectStore = useProjectStore();
  const estimateStore = useEstimateStore();
  const sessionStore = useSessionStore();

  /** State **/
  const [showAddModal, setShowAddModal] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  // Until the project's sessions have loaded, so an empty project's empty state doesn't flash on others
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /** Computed **/
  const tempEstimate = estimateStore.tempEstimate;

  /** Derived **/
  const saveStatus: SaveStatus = hasUnsavedChanges ? SaveStatus.Saving : SaveStatus.Saved;

  const totalWages = useMemo(() => {
    const subtotals = tempEstimate?.session_estimates.map((sessionEstimate) => sessionEstimate.subtotal) ?? [];
    if (subtotals.length) return subtotals.reduce((acc, wage) => (acc ?? 0) + wage);
  }, [tempEstimate?.session_estimates]);

  const grandTotal = useMemo(() => {
    const subtotalsPlusFringe =
      tempEstimate?.session_estimates.map((sessionEstimate) => sessionEstimate.subtotal_plus_fringe) ?? [];
    if (subtotalsPlusFringe.length) return subtotalsPlusFringe.reduce((acc, wage) => (acc ?? 0) + wage);
  }, [tempEstimate?.session_estimates]);

  /** Methods **/

  const chooseSession = (id: TSessionId) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((s) => s !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const createSession = async (scriptId?: TScriptId) => {
    if (!tempEstimate) {
      console.warn('Could not create Session - Temp Estimate is undefined');
      return;
    }

    const projectId = projectStore.currentProject?.id;
    if (!projectId) return;

    const sortedSessions = [...sessions].sort((a, b) => (a.date > b.date ? 1 : -1));
    const nextDate = sortedSessions.length
      ? dayjs(`${sortedSessions[sortedSessions.length - 1].date}T00:00:00`).add(1, 'day')
      : new Date();

    setHasUnsavedChanges(true);
    const session = await sessionStore.createSession(nextDate.toISOString(), projectId, scriptId);
    if (session) {
      setSessions([...sessions, session]);
      await estimateStore.createSessionEstimate(
        `TempSessionEstimate_Session_${session.id}`,
        session.date,
        session.id,
        true
      );
      await estimateStore.refreshTempEstimate();
      setHasUnsavedChanges(false);
    }
  };

  const deleteSession = async (sessionId: TSessionId) => {
    if (!tempEstimate) {
      console.warn('Could not delete Sessions - Temp Estimate is undefined');
      return;
    }

    setHasUnsavedChanges(true);

    await sessionStore.deleteSession(sessionId);

    const sessionIndex = sessions.findIndex((session) => session.id === sessionId);
    if (sessionIndex > -1) {
      sessions.splice(sessionIndex, 1);
      setSessions(sessions);
    }

    const sessionEstimate = tempEstimate.session_estimates.find((se) => se.session && se.session.id === sessionId);
    if (sessionEstimate) {
      await estimateStore.deleteSessionEstimate(sessionEstimate.id, true);
      await estimateStore.refreshTempEstimate();
    }

    setHasUnsavedChanges(false);
  };

  /** Effects **/

  useEffect(() => {
    const getSessions = async () => {
      setSessionsLoaded(false);
      if (!projectStore.currentProject?.id) {
        setSessions([]);
        return;
      }

      if (projectStore.currentProject?.id === estimateStore.tempEstimate?.id) return;

      try {
        // Get or create hidden Estimate for Session Estimator page
        const tempTitle = `${SESSION_ESTIMATOR_TEMP_ESTIMATE_TITLE}${projectStore.currentProject.id}`;
        let estimate = await estimateStore.getEstimateByTitle(tempTitle, 'session_estimates', true);
        if (!estimate) {
          await estimateStore.createEstimate(projectStore.currentProject.id, tempTitle, true);
          estimate = await estimateStore.getEstimateByTitle(tempTitle, 'session_estimates', true);
        }
        if (estimate?.is_hidden) estimateStore.setTempEstimate(estimate);

        await estimateStore.refreshTempEstimate();

        const s = await sessionStore.listSessions(projectStore.currentProject.id);
        setSessions(s || []);
      } catch (err) {
        if ((err as ApiError).status !== 400) console.error(err);
        setSessions([]);
      }
      setSessionsLoaded(true);
    };
    getSessions();
  }, [projectStore.currentProject?.id]);

  useEffect(() => {
    const getLocations = async () => {
      if (!projectStore.currentProject) return;
      const l = await projectStore.getLocationsByProjectId(projectStore.currentProject.id);
      setLocations(l || []);
    };
    getLocations();
  }, []);

  /** Render **/

  return (
    <Box>
      {projectStore.currentProject && (
        <SessionEstimatorHeader
          project={projectStore.currentProject}
          onCreate={createSession}
          saveStatus={saveStatus}
          totalWages={totalWages}
          grandTotal={grandTotal}
        />
      )}
      <Box pad="medium" gap="medium" background="light-1" overflow={{ vertical: 'scroll' }} flex="grow">
        {sessions.map((session, i) => (
          <SessionRow
            sessionId={session.id}
            locations={locations}
            onChecked={() => chooseSession(session.id)}
            onDelete={() => deleteSession(session.id)}
            checked={selected.includes(session.id)}
            onChange={() => setHasUnsavedChanges(true)}
            onSaveComplete={() => setHasUnsavedChanges(false)}
            key={session.id}
            rowIndex={i}
          />
        ))}
        {sessionsLoaded && sessions.length === 0 && (
          <EmptyState
            title="No sessions yet"
            body="Add a shoot day to start estimating session fees."
            actionLabel="New session"
            onAction={() => createSession()}
          />
        )}
      </Box>
      {showAddModal && <CreateSessionModal addSession={createSession} setShowModal={setShowAddModal} />}
    </Box>
  );
});
